'use client';

import {
  type Ref,
  useCallback,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import {
  ChevronDown,
  CloudDownload,
  FolderOpen,
  Loader2,
  Plus,
  Upload
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from '@/lib/toast';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { FILE_DROP_ZONE_ACCEPT } from '@/components/ui/file-drop-zone';
import { cn } from '@/lib/utils';
import { useMatterFiles } from '@/lib/services/files';
import { getDependencies } from '@/lib/services/query-dependencies';
import { AttachmentSelectionDialog } from '@/components/matter-details/tabs/communications/composer/attachment-selection-dialog';
import { AttachFromPmsDialog } from './attach-from-pms-dialog';
import { AddDocumentUploadDialog } from './add-document-upload-dialog';
import { useMatterRequirementAttach } from './use-matter-requirement-attach';
import { usePmsProviderLabel } from './use-pms-provider-label';
import type { SmokeballFile } from '@/lib/services/smokeball-migration/types';
import type { MatterFile } from '@/lib/services/files/types';
import type {
  AddDocumentHandle,
  AddDocumentProps,
  AddDocumentSource
} from './types';

/**
 * The just-imported PMS document as a `MatterFile`, so the receiving surface can
 * name and render it in the same tick (LF-6261).
 *
 * The import lands as a real matter document, but no cached list holds it yet —
 * and the pick callback was captured before the import, so refreshing the
 * matter-files query cannot reach it either. This stands in until that refresh
 * lands and replaces it with the server's own row, so only the fields a surface
 * labels from have to be right; the rest stay empty rather than guessed.
 */
function importedPmsFileToMatterFile(
  fileId: number,
  file: SmokeballFile
): MatterFile {
  return {
    id: fileId,
    originalFilename: file.fileName,
    status: 'available',
    s3Key: '',
    mimeType: '',
    fileType: 'additional-document',
    isCertifiedCopy: false,
    createdAt: file.dateCreated,
    updatedAt: file.dateCreated,
    ...(file.sizeBytes != null && {
      fileSizeKb: Math.max(1, Math.round(file.sizeBytes / 1024)),
      sizeBytes: file.sizeBytes
    })
  } as MatterFile;
}

/**
 * The one shared "add a document" affordance (ADOC-2, LF-6188): a menu exposing
 * up to three sources — **Upload** (via `FileDropZone`), **Choose existing**
 * matter document (reuses the composer picker), and **Import from {PMS}** —
 * behind a single trigger, normalising every source to `onAttached(fileIds)`.
 *
 * Two ways to wire the landing (hybrid contract):
 * - **`target`** (matter requirement) → upload + choose-existing default to the
 *   built-in matter landing (copy/re-type as the target `fileType`), and the PMS
 *   source lands via the same target. This is the redemption path (ADOC-2b).
 * - **callbacks** (`onUpload` / `onPickExisting`) → the caller owns the landing;
 *   choose-existing is a plain reference. This is the beneficiary email-attachment
 *   path (a different upload endpoint, fileId-only, no copy).
 *
 * PMS is offered whenever a provider resolves — either the `pmsProviderLabel`
 * prop or, when that is omitted, the matter's own `metadata.pms.source`
 * (LF-6261) — and the import has somewhere to land (a `target` or a `pickAction`).
 *
 * Trigger: by default the component renders its own menu button. Pass
 * `open`/`onOpenChange` for **controlled-open** mode — no built-in trigger; the
 * caller owns the trigger and the flow opens from that state (a compact source
 * chooser appears first when more than one source is offered). Pass
 * `variant="split-button"` for the compact **[Upload | ▾]** control (LF-6203) —
 * a one-click Upload picker + a source ▾ menu, with an imperative `ref` for a
 * caller-owned drop surface and `onUploadingChange` for caller-owned progress.
 */
export function AddDocument({
  matterId,
  sources,
  target,
  onUpload,
  onPickExisting,
  onAttached,
  files,
  documentsLabel = 'Matter documents',
  multiple,
  allowCertifiedCopy,
  open,
  onOpenChange,
  variant = 'menu',
  accept = FILE_DROP_ZONE_ACCEPT.documents.accept,
  onUploadingChange,
  alreadySelectedFileIds,
  pmsProviderLabel,
  disabled,
  triggerLabel = 'Add document',
  triggerIcon: TriggerIcon = Plus,
  triggerVariant = 'outline',
  triggerSize = 'sm',
  className,
  ref
}: AddDocumentProps & { ref?: Ref<AddDocumentHandle> }) {
  // Which source's sub-dialog is currently showing. In uncontrolled mode the menu
  // sets this directly; in controlled mode it's what the source chooser resolves to.
  const [openSource, setOpenSource] = useState<AddDocumentSource | null>(null);

  const queryClient = useQueryClient();
  const adapter = useMatterRequirementAttach(matterId, target);
  const uploadAction = onUpload ?? adapter.upload;
  // The requirement adapter ignores the second argument; annotate so the shared
  // call below can still hand every pick its resolved files.
  const pickAction: AddDocumentProps['onPickExisting'] =
    onPickExisting ?? adapter.pickExisting;

  // Split-button mode (LF-6203): a one-click OS picker on the primary button and
  // an imperative drop-handle, both routing straight to the Upload action (no
  // dialog). `busy` drives the in-place "Uploading…" state and `onUploadingChange`
  // lets a caller-owned surface (the requirement row) show its own spinner + disable
  // its drop target while in flight.
  const isSplitButton = variant === 'split-button';
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  const runUpload = useCallback(
    async (uploadFiles: File[]): Promise<void> => {
      if (!uploadAction || uploadFiles.length === 0) return;
      setBusy(true);
      onUploadingChange?.(true);
      try {
        const ids = await uploadAction(uploadFiles, {});
        if (ids.length > 0) await onAttached?.(ids);
      } catch {
        toast.error('Failed to upload document');
      } finally {
        setBusy(false);
        onUploadingChange?.(false);
      }
    },
    [uploadAction, onAttached, onUploadingChange]
  );

  useImperativeHandle(ref, () => ({ uploadFiles: runUpload }), [runUpload]);

  const openPicker = () => fileInputRef.current?.click();

  // Existing-document picker list: the caller's override (e.g. a lead's client docs)
  // when supplied, else the matter's own files (the dialog is presentational).
  const { data: filesData } = useMatterFiles(matterId);
  const pickFiles = files ?? filesData?.files ?? [];

  // PMS needs a provider, plus somewhere to route the imported file: either a
  // `target` (redemption lands it against the requirement) or a `pickAction`
  // (callback mode — the import lands as a neutral matter document, then is
  // referenced exactly like a choose-existing pick). ADOC-7 (LF-6245).
  //
  // The provider is the caller's label when it passes one, else the matter's own
  // PMS source — so a surface offering `pms` needs no connected-matter gate of
  // its own, and the source stays hidden when the matter isn't synced (LF-6261).
  const matterPmsProviderLabel = usePmsProviderLabel(
    matterId,
    sources.includes('pms') && !pmsProviderLabel
  );
  const providerLabel = pmsProviderLabel ?? matterPmsProviderLabel;
  const canPms =
    sources.includes('pms') && !!providerLabel && (!!target || !!pickAction);
  const visibleSources = sources.filter((s) => s !== 'pms' || canPms);
  // In callback mode there is no requirement target, so PMS lands a neutral
  // `additional-document` (a plain matter file, no requirement association);
  // redemption keeps its per-requirement target.
  const pmsTarget = target ?? {
    fileType: 'additional-document' as const,
    label: 'a document'
  };

  // Controlled-open (LF-6189): the caller owns the trigger and toggles `open`.
  const isControlled = open !== undefined;
  // A single-source controlled flow opens that source directly (no chooser); a
  // multi-source one shows the chooser until a source is picked (openSource set).
  const singleSource = visibleSources.length === 1;
  const activeSource: AddDocumentSource | null = isControlled
    ? open
      ? singleSource
        ? visibleSources[0]
        : openSource
      : null
    : openSource;
  const showChooser =
    isControlled && !!open && !singleSource && openSource === null;

  // End the whole flow: reset the sub-dialog and, when controlled, tell the caller
  // the trigger state is closed.
  const closeAll = () => {
    setOpenSource(null);
    if (isControlled) onOpenChange?.(false);
  };
  // A sub-dialog's onOpenChange: close ends the flow; open is already reflected.
  const handleSubDialogOpenChange =
    (source: AddDocumentSource) => (next: boolean) => {
      if (next) setOpenSource(source);
      else closeAll();
    };

  // Every pick hands the caller the files behind the ids, not just the ids —
  // see `onPickExisting` in ./types.ts for why an id-only hand-off breaks the
  // PMS import (LF-6261).
  const handlePick = async (
    fileIds: number[],
    pickedFiles: readonly MatterFile[]
  ) => {
    if (fileIds.length === 0 || !pickAction) return;
    const resultIds = await pickAction(fileIds, pickedFiles);
    if (resultIds.length > 0) await onAttached?.(resultIds);
  };

  const sourceLabel = (source: AddDocumentSource) =>
    source === 'upload'
      ? 'Upload from computer'
      : source === 'existing'
        ? 'Choose existing document'
        : `Attach from ${providerLabel}`;

  // Return the source's icon as JSX (not a component reference) so it isn't flagged
  // as a component created during render (@eslint-react/static-components).
  const sourceIcon = (source: AddDocumentSource, cls: string) => {
    if (source === 'upload') return <Upload className={cls} />;
    if (source === 'existing') return <FolderOpen className={cls} />;
    return <CloudDownload className={cls} />;
  };

  const menuItemFor = (source: AddDocumentSource) => (
    <DropdownMenuItem key={source} onSelect={() => setOpenSource(source)}>
      {sourceIcon(source, 'mr-2 h-3.5 w-3.5')}
      {sourceLabel(source)}
    </DropdownMenuItem>
  );

  const trigger =
    visibleSources.length === 1 ? (
      <Button
        type="button"
        variant={triggerVariant}
        size={triggerSize}
        disabled={disabled}
        onClick={() => setOpenSource(visibleSources[0])}
        className={className}
      >
        <TriggerIcon className="mr-1.5 h-3.5 w-3.5" />
        {triggerLabel}
      </Button>
    ) : (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant={triggerVariant}
            size={triggerSize}
            disabled={disabled}
            className={className}
          >
            <TriggerIcon className="mr-1.5 h-3.5 w-3.5" />
            {triggerLabel}
            <ChevronDown className="ml-1 h-3.5 w-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {visibleSources.map(menuItemFor)}
        </DropdownMenuContent>
      </DropdownMenu>
    );

  // The compact [Upload | ▾] control (LF-6203). Primary click = one-click OS
  // picker straight into Upload (no dialog); the ▾ lists the other sources. While
  // an upload is in flight it collapses to a disabled "Uploading…" button (the
  // caller row also reflects this via onUploadingChange).
  const hasUploadSource = visibleSources.includes('upload');
  const otherSources = visibleSources.filter((s) => s !== 'upload');
  const primaryClick = hasUploadSource
    ? openPicker
    : () => setOpenSource(otherSources[0]);
  const splitButtonTrigger = busy ? (
    <Button
      type="button"
      variant={triggerVariant}
      size={triggerSize}
      disabled
      className={className}
    >
      <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
      Uploading…
    </Button>
  ) : otherSources.length === 0 ? (
    // Upload is the only source → a plain one-click button, no ▾.
    <Button
      type="button"
      variant={triggerVariant}
      size={triggerSize}
      disabled={disabled}
      onClick={primaryClick}
      className={className}
    >
      <TriggerIcon className="mr-1.5 h-3.5 w-3.5" />
      {triggerLabel}
    </Button>
  ) : (
    <div className={cn('flex', className)}>
      <Button
        type="button"
        variant={triggerVariant}
        size={triggerSize}
        disabled={disabled}
        onClick={primaryClick}
        className="rounded-r-none border-r-0 focus-visible:ring-0!"
      >
        <TriggerIcon className="mr-1.5 h-3.5 w-3.5" />
        {triggerLabel}
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant={triggerVariant}
            size={triggerSize}
            disabled={disabled}
            className="rounded-l-none px-2"
            aria-label="More upload options"
          >
            <ChevronDown className="h-3.5 w-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {hasUploadSource && (
            <DropdownMenuItem onSelect={openPicker}>
              <Upload className="mr-2 h-3.5 w-3.5" />
              Upload from computer
            </DropdownMenuItem>
          )}
          {otherSources.map((source) => (
            <DropdownMenuItem
              key={source}
              onSelect={() => setOpenSource(source)}
            >
              {sourceIcon(source, 'mr-2 h-3.5 w-3.5')}
              {sourceLabel(source)}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );

  return (
    <>
      {/* Built-in trigger only in uncontrolled mode; controlled callers own theirs. */}
      {!isControlled && (isSplitButton ? splitButtonTrigger : trigger)}

      {/* Split-button's one-click / imperative upload picker (no dialog). */}
      {isSplitButton && (
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          className="hidden"
          onChange={(e) => {
            const selected = Array.from(e.target.files ?? []);
            e.target.value = '';
            if (selected.length > 0) void runUpload(selected);
          }}
        />
      )}

      {/* Controlled multi-source: pick a source before its dialog opens. */}
      {showChooser && (
        <Dialog open onOpenChange={(next) => !next && closeAll()}>
          <DialogContent className="max-w-xs gap-3 p-4">
            <DialogHeader className="space-y-0">
              <DialogTitle className="text-sm font-semibold">
                {triggerLabel}
              </DialogTitle>
              <DialogDescription className="sr-only">
                Choose how to add a document.
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-1.5">
              {visibleSources.map((source) => (
                <Button
                  key={source}
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setOpenSource(source)}
                  className="text-12px h-9 justify-start"
                >
                  {sourceIcon(source, 'mr-2 h-3.5 w-3.5')}
                  {sourceLabel(source)}
                </Button>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Split-button uploads go through the one-click picker, not this dialog. */}
      {uploadAction && !isSplitButton && (
        <AddDocumentUploadDialog
          open={activeSource === 'upload'}
          onOpenChange={handleSubDialogOpenChange('upload')}
          multiple={multiple}
          allowCertifiedCopy={allowCertifiedCopy}
          upload={uploadAction}
          onAttached={onAttached}
        />
      )}

      {sources.includes('existing') && (
        <AttachmentSelectionDialog
          open={activeSource === 'existing'}
          onOpenChange={handleSubDialogOpenChange('existing')}
          files={pickFiles}
          documentsLabel={documentsLabel}
          alreadySelected={alreadySelectedFileIds ?? []}
          onAdd={(ids) =>
            handlePick(
              ids,
              pickFiles.filter((f) => ids.includes(f.id))
            )
          }
        />
      )}

      {/* `canPms` already implies `providerLabel` is set; the extra check here
          is just so TS narrows it to `string` for the dialog below. */}
      {canPms && providerLabel && (
        <AttachFromPmsDialog
          open={activeSource === 'pms'}
          onOpenChange={handleSubDialogOpenChange('pms')}
          matterId={matterId}
          providerLabel={providerLabel}
          target={{ ...pmsTarget, label: pmsTarget.label ?? 'a document' }}
          onAttached={async (importedFileId, importedFile) => {
            // The import is a real matter document now, so every list that shows
            // matter documents is stale — refresh them through the single-source
            // resolver rather than a hand-rolled key list (LF-3021).
            for (const key of getDependencies('document', matterId)) {
              void queryClient.invalidateQueries({ queryKey: key });
            }

            if (target) {
              // target mode (redemption): the import already landed against the requirement
              await onAttached?.([]);
            } else {
              // callback mode: the imported file is now a plain matter document —
              // reference it exactly as a choose-existing pick. The refresh above
              // cannot help the caller name it (this callback closed over the
              // pre-import lists), so hand the document across (LF-6261).
              await handlePick(
                [importedFileId],
                [importedPmsFileToMatterFile(importedFileId, importedFile)]
              );
            }
          }}
        />
      )}
    </>
  );
}
